// PostHog configuration and utilities
import posthog from 'posthog-js';
import type { CaptureResult, PostHogConfig } from 'posthog-js';

// Type definitions
export type UserProperties = {
  email?: string;
  full_name?: string;
  name?: string;
  provider?: string;
  phoneVerified?: boolean;
  points?: number;
  role?: string;
  isAdmin?: boolean;
  createdAt?: string | Date;
  [key: string]: unknown;
};

export type EventProperties = {
  [key: string]: string | number | boolean | Date | null | undefined;
};

// Standard props attached to any store-scoped event (see .planning/notes/POSTHOG-EVENT-MAP.md)
export type StoreEventProps = EventProperties & {
  store_id?: string;
  store_slug?: string;
  category_slug?: string;
  store_verified?: boolean;
  store_status?: string;
};

// Structural subset of Store — keeps this module free of an api/ import.
export type TrackableStore = {
  _id?: string;
  slug?: string;
  verified?: boolean;
  status?: string;
  category?: { slug?: string };
  prominentCategory?: { category?: { slug?: string } };
};

// Emitted when a store has no category at all (~5% of the published catalogue).
// Without it the event carries no category_slug, which reads in PostHog as
// "None" — indistinguishable from a capture that silently broke.
export const UNCATEGORIZED = "uncategorized";

// `prominentCategory` wins over the legacy single `category`, matching what the
// store card and header actually display. Every store-scoped event must resolve
// the slug through here: mixing the two orderings splits one store across two
// breakdown values.
export const resolveCategorySlug = (store?: TrackableStore | null): string =>
  store?.prominentCategory?.category?.slug ?? store?.category?.slug ?? UNCATEGORIZED;

// The single source of the standard store props. Returns {} for a missing store
// so a not-yet-loaded store never reports itself as uncategorized.
export const storeEventProps = (store?: TrackableStore | null): StoreEventProps =>
  store
    ? {
        store_id: store._id,
        store_slug: store.slug,
        category_slug: resolveCategorySlug(store),
        store_verified: Boolean(store.verified),
        store_status: store.status,
      }
    : {};

export type GroupProperties = {
  [key: string]: string | number | boolean | Date | null | undefined;
};

// ─── Secret redaction ───
// The email unsubscribe and verify-email links carry a signed bearer token in `?token=`. The
// pages strip it from the URL on mount, but anything captured before that (the first $pageview,
// $initial_current_url person props, a referrer) would still store it. Every string property that
// carries a `token=` query param is redacted before the event leaves the browser.
const TOKEN_PARAM_RE = /([?&#]token=)[^&#\s]*/gi;
export const REDACTED = '[redacted]';

export const redactUrlTokens = (value: string): string =>
  value.replace(TOKEN_PARAM_RE, `$1${REDACTED}`);

const redactProps = (props: Record<string, unknown> | undefined): void => {
  if (!props) return;
  for (const key of Object.keys(props)) {
    const value = props[key];
    if (typeof value === 'string' && /token=/i.test(value)) props[key] = redactUrlTokens(value);
  }
};

// posthog `before_send` hook. Mutates and returns the same result; never drops an event.
export const redactCaptureTokens = (cr: CaptureResult | null): CaptureResult | null => {
  if (!cr) return cr;
  redactProps(cr.properties as Record<string, unknown> | undefined);
  redactProps(cr.$set as Record<string, unknown> | undefined);
  redactProps(cr.$set_once as Record<string, unknown> | undefined);
  return cr;
};

// Shared init options — consumed by PostHogProvider in pages/+Layout.tsx (the
// live init path) and by initPostHog() (legacy pre-Vike entry).
export const posthogOptions: Partial<PostHogConfig> = {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  defaults: "2025-05-24",
  // Keep signed email-link tokens out of $current_url, $referrer and person props.
  before_send: redactCaptureTokens,
  // Enable session replay
  session_recording: {
    recordCrossOriginIframes: true,
    // Mask sensitive inputs by default
    maskAllInputs: false,
    maskInputOptions: {
      password: true,
      email: false,
    },
  },
  // Enable autocapture for click tracking
  autocapture: {
    css_selector_allowlist: [
      '[data-ph-capture]',
      '.btn',
      '.button',
      'button',
      'a',
      '[role="button"]',
    ],
  },
  // Enable feature flags
  bootstrap: {
    featureFlags: {},
  },
  loaded: () => {
    if (import.meta.env.DEV) {
      console.log('PostHog loaded in development mode');
    }
  },
};

// Initialize PostHog with session replay
export const initPostHog = () => {
  if (typeof window !== 'undefined') {
    posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, posthogOptions);
  }
};

// User identification
export const identifyUser = (userId: string, userProperties: UserProperties = {}) => {
  if (typeof window !== 'undefined' && posthog) {
    posthog.identify(userId, {
      email: userProperties.email,
      name: userProperties.full_name || userProperties.name,
      provider: userProperties.provider,
      phoneVerified: userProperties.phoneVerified,
      points: userProperties.points,
      role: userProperties.role,
      isAdmin: userProperties.isAdmin,
      createdAt: userProperties.createdAt,
      ...userProperties,
    });
    
    // Set user properties for session replay
    posthog.setPersonProperties({
      email: userProperties.email,
      name: userProperties.full_name || userProperties.name,
      provider: userProperties.provider,
      role: userProperties.role || 'user',
      signupDate: userProperties.createdAt,
    });
  }
};

// Reset user (on logout)
export const resetUser = () => {
  if (typeof window !== 'undefined' && posthog) {
    posthog.reset();
  }
};

// Track custom events
export const trackEvent = (eventName: string, properties: EventProperties = {}) => {
  if (typeof window !== 'undefined' && posthog) {
    posthog.capture(eventName, properties);
  }
};

// Track page views
export const trackPageView = (pageName: string, properties: EventProperties = {}) => {
  if (typeof window !== 'undefined' && posthog) {
    posthog.capture('$pageview', {
      $current_url: window.location.href,
      page_name: pageName,
      ...properties,
    });
  }
};

// Group identification for organizations/companies
export const identifyGroup = (groupType: string, groupKey: string, groupProperties: GroupProperties = {}) => {
  if (typeof window !== 'undefined' && posthog) {
    posthog.group(groupType, groupKey, groupProperties);
  }
};

// Feature flag evaluation
export const isFeatureEnabled = (flagKey: string, defaultValue: boolean = false): boolean => {
  if (typeof window !== 'undefined' && posthog) {
    return posthog.isFeatureEnabled(flagKey) ?? defaultValue;
  }
  return defaultValue;
};

// Get feature flag value
export const getFeatureFlag = <T = unknown>(flagKey: string, defaultValue: T | null = null): T | null => {
  if (typeof window !== 'undefined' && posthog) {
    return (posthog.getFeatureFlag(flagKey) as T) ?? defaultValue;
  }
  return defaultValue;
};

// Event types for type safety
export const PostHogEvents = {
  // Authentication
  USER_SIGNED_UP: 'user_signed_up',
  USER_SIGNED_IN: 'user_signed_in',
  USER_SIGNED_OUT: 'user_signed_out',
  USER_PHONE_VERIFIED: 'user_phone_verified',
  
  // Store interactions
  STORE_VIEWED: 'store_viewed',
  STORE_SEARCHED: 'store_searched',
  STORE_ADDED: 'store_added',
  STORE_SCRAPED: 'store_scraped',
  
  // Review interactions
  REVIEW_CREATED: 'review_created',
  REVIEW_VIEWED: 'review_viewed',
  REVIEW_LIKED: 'review_liked',
  
  // Navigation
  PAGE_VIEWED: 'page_viewed',
  CATEGORY_VIEWED: 'category_viewed',
  // Category page data blocks (quick-261010-fvy): recent reviews, most reviewed, related.
  CATEGORY_INSIGHT_CLICKED: 'category_insight_clicked',
  // Category page search box (quick-261010-oyg): a settled semantic query over the category's reviews.
  CATEGORY_REVIEWS_SEARCHED: 'category_reviews_searched',
  SEARCH_PERFORMED: 'search_performed',
  
  // Gamification
  POINTS_EARNED: 'points_earned',
  LEVEL_UP: 'level_up',
  
  // Social auth
  GOOGLE_AUTH_INITIATED: 'google_auth_initiated',
  FACEBOOK_AUTH_INITIATED: 'facebook_auth_initiated',
  PHONE_AUTH_INITIATED: 'phone_auth_initiated',
  
  // Errors
  ERROR_OCCURRED: 'error_occurred',
  API_ERROR: 'api_error',

  // Conversion: outbound to merchant + sharing
  STORE_CONTACT_CLICKED: 'store_contact_clicked',
  STORE_SHARED: 'store_shared',

  // Reader opens the excluded («مُستبعدة») review tab on a store with a detected campaign
  STORE_FLAGGED_REVIEWS_TAB_VIEWED: 'store_flagged_reviews_tab_viewed',

  // Store submission (AddStoreModal scrape + tokenized /submit form)
  STORE_SUBMITTED: 'store_submitted',
  STORE_SUBMISSION_COMPLETED: 'store_submission_completed',
  STORE_DUPLICATE_VIEW_CLICKED: 'store_duplicate_view_clicked',
  SUBMISSION_FORM_STEP_COMPLETED: 'submission_form_step_completed',
  SUBMISSION_FORM_SUBMITTED: 'submission_form_submitted',

  // Review funnel
  REVIEW_CTA_CLICKED: 'review_cta_clicked',
  REVIEW_REPORTED: 'review_reported',
  REVIEW_COMMENT_ADDED: 'review_comment_added',
  // Reader opens the original post behind an external (scraped/imported) review.
  REVIEW_SOURCE_CLICKED: 'review_source_clicked',
  // Reader shares ONE review (the «مشاركة» action on a review card) via a Shlink short link.
  REVIEW_SHARED: 'review_shared',
  // The post-conversion step: the cross-sell CTA on the inline thank-you state that replaces the
  // form after a submit. This is the second-session hook, measured on both submit surfaces.
  REVIEW_THANKYOU_SEARCH_CLICKED: 'review_thankyou_search_clicked',
  // The OTHER thing a contributor can do on the thank-you state: go back and add the detail they
  // skipped. Separate from the cross-sell above because it answers a different question — whether
  // a points delta stated after the fact actually recovers a photo or a written experience.
  REVIEW_THANKYOU_UPSELL_CLICKED: 'review_thankyou_upsell_clicked',
  // The tier chip / author name on a review card, tapped through to /u/@id. Measures whether
  // contribution identity is something readers actually follow, which is the premise of the
  // public profile existing at all.
  PROFILE_TIER_CHIP_CLICKED: 'profile_tier_chip_clicked',
  // /u/@id. `viewer_role` is the reason this event exists: a profile read by its owner and a
  // profile read by a stranger are two different products sharing one URL.
  PUBLIC_PROFILE_VIEWED: 'public_profile_viewed',
  PROFILE_HISTORY_TAB_OPENED: 'profile_history_tab_opened',

  // Public fake-review blacklist (/blacklist + /fake-reviews).
  // NO PII ON ANY OF THESE. `page_id` is a public Facebook id and is fine; the visitor's typed
  // query is not, so the search event carries `query_length` and never the query itself.
  BLACKLIST_SEARCH_SUBMITTED: 'blacklist_search_submitted',
  BLACKLIST_CATEGORY_SELECTED: 'blacklist_category_selected',
  BLACKLIST_FILTER_APPLIED: 'blacklist_filter_applied',
  BLACKLIST_PAGE_MODAL_OPENED: 'blacklist_page_modal_opened',
  BLACKLIST_FB_REVIEWS_TAB_CLICKED: 'blacklist_fb_reviews_tab_clicked',
  BLACKLIST_REQUEST_SUBMITTED: 'blacklist_request_submitted',
  BLACKLIST_STORY_CTA_CLICKED: 'blacklist_story_cta_clicked',
  // Autocomplete on the hero search. Carries `query_length`, NEVER the query and never the page
  // name — same PII rule as `blacklist_search_submitted` above, and it matters more here because
  // this one fires while the visitor is still typing.
  BLACKLIST_SUGGESTION_CLICKED: 'blacklist_suggestion_clicked',
  // One per successful infinite-scroll fetch, not per render.
  BLACKLIST_LIST_EXTENDED: 'blacklist_list_extended',

  // Blog (Phase 51). Captured inside the shared blog link primitives (BlogAnchor, BlogTrustLink)
  // with a required `surface`, never at call sites. Props are slugs, surface, target type and
  // storeEventProps only (no PII).
  BLOG_POST_VIEWED: 'blog_post_viewed',
  BLOG_LINK_CLICKED: 'blog_link_clicked',

  // Auth funnel
  AUTH_MODAL_SHOWN: 'auth_modal_shown',
  PHONE_OTP_SENT: 'phone_otp_sent',
  PHONE_OTP_RESENT: 'phone_otp_resent',

  // Search & chat
  SEARCH_SUGGESTION_CLICKED: 'search_suggestion_clicked',
  CHAT_CHANNEL_CLICKED: 'chat_channel_clicked',

  // Web chat panel (Phase 38). Note what is NOT here: the panel header's WhatsApp escape hatch
  // reuses CHAT_CHANNEL_CLICKED above with `surface: "chat_panel_header"`. That name is the only
  // reason the WhatsApp funnel is measurable today, and minting a second name at the exact moment
  // the surface moved would fork the funnel and render the move as a drop in WhatsApp intent.
  CHAT_PANEL_OPENED: 'chat_panel_opened',
  CHAT_PANEL_CLOSED: 'chat_panel_closed',
  CHAT_SUGGESTION_CLICKED: 'chat_suggestion_clicked',
  CHAT_MESSAGE_SENT: 'chat_message_sent',
  CHAT_RESPONSE_STREAMED: 'chat_response_streamed',
  CHAT_GENERATION_STOPPED: 'chat_generation_stopped',
  CHAT_LIMIT_REACHED: 'chat_limit_reached',
  CHAT_LOGIN_NUDGE_CLICKED: 'chat_login_nudge_clicked',
  CHAT_STREAM_RECONNECTED: 'chat_stream_reconnected',

  // Web chat store carousel (Phase 39, WEB-03). Both carry slugs and counts only, never a card
  // title: a card title is scraped store prose, and prose has never been an event property here.
  CHAT_STORE_CARD_CLICKED: 'chat_store_card_clicked',
  CHAT_STORE_CAROUSEL_SHOWN: 'chat_store_carousel_shown',
  // The card's contact icon row (39-08). `contact_kind` is one of three literals
  // ('phone' | 'whatsapp' | 'maps'); the destination itself is never a property — a tel: number
  // is PII-adjacent and a maps query embeds the store name, which is prose.
  CHAT_STORE_CONTACT_CLICKED: 'chat_store_contact_clicked',

  // Claim funnel
  CLAIM_STARTED: 'claim_started',
  CLAIM_STORE_SELECTED: 'claim_store_selected',
  CLAIM_OTP_SENT: 'claim_otp_sent',
  CLAIM_OTP_VERIFIED: 'claim_otp_verified',
  CLAIM_OTP_FALLBACK: 'claim_otp_fallback',
  CLAIM_EVIDENCE_SUBMITTED: 'claim_evidence_submitted',
  CLAIM_APPROVED_VIEWED: 'claim_approved_viewed',
  CLAIM_BANNER_CLICKED: 'claim_banner_clicked',
  CLAIM_BANNER_DISMISSED: 'claim_banner_dismissed',

  // Merchant activation
  WIDGET_SNIPPET_COPIED: 'widget_snippet_copied',
  WIDGET_CREATED: 'widget_created',
  RATE_LINK_COPIED: 'rate_link_copied',
  SHOPIFY_INSTALL_CLICKED: 'shopify_install_clicked',
  MERCHANT_REVIEW_REPLIED: 'merchant_review_replied',

  // Notifications (Phase 46). Public surfaces only: the admin bell and the moderator view never
  // capture these (D-12 of Phase 45). NO PII: only the notification type, channel, surface and a
  // boolean, never a name, email, phone or the notification text.
  NOTIFICATION_CLICKED: 'notification_clicked',
  NOTIFICATION_PREFS_CHANGED: 'notification_prefs_changed',
  PUSH_PERMISSION_GRANTED: 'push_permission_granted',
  WHATSAPP_OPTIN_CHANGED: 'whatsapp_optin_changed',

  // New-user onboarding (Phase 49). Captured inside the shared components (WelcomeChecklist,
  // PhoneAuth finish step, OnboardingReturnBanner), never at call sites. NO PII: triggers, counts,
  // booleans and storeEventProps only. The admin Onboarding tab captures nothing.
  WELCOME_CARD_SHOWN: 'welcome_card_shown',
  WELCOME_CARD_ITEM_CLICKED: 'welcome_card_item_clicked',
  WELCOME_CARD_DISMISSED: 'welcome_card_dismissed',
  SIGNUP_FINISH_SUBMITTED: 'signup_finish_submitted',
  RETURN_BANNER_SHOWN: 'return_banner_shown',
  RETURN_BANNER_REVIEW_CLICKED: 'return_banner_review_clicked',
  RETURN_BANNER_DISMISSED: 'return_banner_dismissed',
} as const;

export type PostHogEventType = typeof PostHogEvents[keyof typeof PostHogEvents];

export default posthog;